import { i18n } from "@/i18n";

export class GenericError extends Error {
  code: string;
  status: number | undefined;

  constructor(message = "An error occured.", code = "error") {
    super(message);
    this.name = this.constructor.name;
    this.code = code;
    this.status = undefined;
  }
}

export class NoInternetError extends GenericError {
  constructor(
    message = "Internet could not be reached.",
    code = "no_internet"
  ) {
    super(message, code);
  }
}

export class BadRequestError extends GenericError {
  fields: Record<string, string>;

  constructor(
    message: string | undefined = undefined,
    code = "bad_request",
    fields = {}
  ) {
    if (message == null) message = i18n.t("errors.badRequest");
    super(message, code);
    this.status = 400;
    this.fields = fields;
  }
}

export class LicenseLimitReachedError extends GenericError {
  limits: { [limit: string]: number };

  constructor(
    message: string | undefined = undefined,
    code = "license_limit_reached",
    limits = {}
  ) {
    if (message == null) message = i18n.t("errors.licenseLimitReached");
    super(message, code);
    this.status = 403;
    this.limits = limits;
  }
}

export class ModelLimitReachedError extends LicenseLimitReachedError {
  limits: { [limit: string]: number } & { models: number };

  constructor(
    modelLimit?: number,
    message: string | undefined = undefined,
    code = "model_limit_reached",
  ) {
    const limits = { models: modelLimit ?? 0 };
    const defaultMessage = i18n.t("errors.modelLimitReached", { modelLimit: limits.models + " " + i18n.t("objectNames.model", limits.models) });
    super(message || defaultMessage, code, limits);
    this.limits = { ...limits };
  }
}

export class AccessDeniedError extends GenericError {
  constructor(message: string | undefined = undefined, code = "access_denied") {
    if (message == null) message = i18n.t("errors.access_denied");
    super(message, code);
    this.status = 403;
  }
}

export class WrongPasswordError extends AccessDeniedError {
  constructor(
    message: string | undefined = undefined,
    code = "wrong_password"
  ) {
    if (message == null) message = i18n.t("errors.wrongPassword");
    super(message, code);
    this.status = 403;
  }
}

export class ServerError extends GenericError {
  constructor(message: string | undefined = undefined, code = "server_error") {
    if (message == null) message = i18n.t("errors.serverError");
    super(message, code);
    this.status = 500;
  }
}

export class NotFoundError extends GenericError {
  constructor(message: string | undefined = undefined, code = "not_found") {
    if (message == null) message = i18n.t("errors.notFound");
    super(message, code);
    this.status = 404;
  }
}

export class NotAuthenticatedError extends GenericError {
  constructor(message = "Not authenticated.", code = "not_authenticated") {
    super(message, code);
    this.status = 401;
  }
}

// More detailed errors:

export class InvalidTokenError extends NotAuthenticatedError {
  constructor(message = "Token is expired.", code = "invalid_token") {
    super(message, code);
  }
}

export class NoTokenError extends InvalidTokenError {
  constructor(message = "Token must be set.", code = "no_token") {
    super(message, code);
  }
}

export class NoRefreshTokenError extends NoTokenError {
  constructor(message = "No refresh token.", code = "no_refresh_token") {
    super(message, code);
  }
}

export class UserNotFoundError extends NotFoundError {
  constructor(message = "User not found.", code = "user_not_found") {
    super(message, code);
  }
}

export class ModelNotFoundError extends NotFoundError {
  constructor(message = "Model not found.", code = "model_not_found") {
    super(message, code);
  }
}

export class ModeldataNotFoundError extends NotFoundError {
  constructor(message = "Modeldata not found.", code = "modeldata_not_found") {
    super(message, code);
  }
}

export class ModelNotReadableError extends GenericError {
  constructor(message = "Model not readable.", code = "model_not_readable") {
    super(message, code);
  }
}

export class ConflictError extends GenericError {
  conflictingEntry: { created: string; id: number } | null;
  constructor(
    message = "Conflict Error",
    code = "conflict",
    conflictingEntry: { created: string; id: number } | null = null
  ) {
    super(message, code);
    this.status = 409;
    this.conflictingEntry = conflictingEntry;
  }
}

export function createError(
  errorCode: string,
  message: string | undefined = undefined
) {
  switch (errorCode) {
    case "model_not_readable":
      return new ModelNotReadableError(message);

    case "modeldata_not_found":
      return new ModeldataNotFoundError(message);

    case "model_not_found":
      return new ModelNotFoundError(message);

    case "user_not_found":
      return new UserNotFoundError(message);

    case "no_token":
      return new NoTokenError(message);

    case "not_authenticated":
      return new NotAuthenticatedError(message);

    case "access_denied":
      return new AccessDeniedError(message);

    default:
      return new GenericError(message);
  }
}
